import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';
import { Ride } from '../../types';

const RidesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [rideTypeFilter, setRideTypeFilter] = useState<string>('');
  const [cities, setCities] = useState<string[]>([]);

  // Check if user can create rides
  const canCreateRide = user?.role === 'STORE_MANAGER' || 
                       user?.role === 'STORE_ADMIN' || 
                       user?.role === 'COMMUNITY_MANAGER' ||
                       user?.role === 'ADMIN' ||
                       user?.role === 'SYSTEM_ADMIN' ||
                       user?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchRides();
  }, [statusFilter, cityFilter, rideTypeFilter]);

  const fetchCities = async () => {
    try {
      const response = await api.get('/cities');
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchRides = async () => {
    try {
      const params: any = { page: 1, limit: 20, status: statusFilter };
      // Pass city filter: empty string means "all cities", otherwise use the selected city
      // The backend will handle empty string as "show all cities" (no city filter)
      if (cityFilter === '') {
        params.city = 'all'; // Explicitly tell backend to show all cities
      } else {
        params.city = cityFilter;
      }
      // Pass ride type filter: empty string means "all types", otherwise use the selected ride type
      // The backend will handle empty string as "show all types" (no ride type filter)
      if (rideTypeFilter === '') {
        params.rideType = 'all'; // Explicitly tell backend to show all ride types
      } else {
        params.rideType = rideTypeFilter;
      }
      const response = await api.get('/rides', { params });
      setRides(response.data.rides || []);
    } catch (error) {
      console.error('Error fetching rides:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('rides.title')}</h1>
        <div className="flex items-center space-x-3">
          <Link
            to="/rides/my-rides"
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {t('rides.myRides')}
          </Link>
          {canCreateRide && (
            <Link
              to="/rides/create"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('rides.createRide')}
            </Link>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div className="flex gap-4 flex-1">
          <div className="flex-1 max-w-xs">
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('rides.allCities') || 'All Cities'}</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 max-w-xs">
            <select
              value={rideTypeFilter}
              onChange={(e) => setRideTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{t('rides.allTypes') || 'All Types'}</option>
              <option value="ROAD">{t('rides.road') || 'Road'}</option>
              <option value="GRAVEL">{t('rides.gravel') || 'Gravel'}</option>
              <option value="MTB">{t('rides.mtb') || 'MTB'}</option>
            </select>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setStatusFilter('upcoming')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              statusFilter === 'upcoming'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('rides.upcoming')}
          </button>
          <button
            onClick={() => setStatusFilter('past')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              statusFilter === 'past'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('rides.past')}
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              statusFilter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('rides.all')}
          </button>
        </div>
      </div>

      {rides.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {(() => {
              // Show specific message based on active filters
              if (rideTypeFilter && rideTypeFilter !== '') {
                const rideTypeLabel = t(`rides.${rideTypeFilter.toLowerCase()}`) || rideTypeFilter;
                return t('rides.noRidesOfType', { rideType: rideTypeLabel }) || `No ${rideTypeLabel} rides available`;
              } else if (cityFilter && cityFilter !== '') {
                return t('rides.noRidesInCity', { city: cityFilter }) || `No rides available in ${cityFilter}`;
              } else {
                return t('rides.noUpcomingRides') || 'No upcoming rides available';
              }
            })()}
          </h3>
          <p className="text-gray-500 mb-4">
            {t('rides.checkBackLater') || 'Check back later for new rides!'}
          </p>
          {(cityFilter && cityFilter !== '') || (rideTypeFilter && rideTypeFilter !== '') ? (
            <div className="flex gap-2 justify-center">
              {cityFilter && cityFilter !== '' && (
                <button
                  onClick={() => setCityFilter('')}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {t('rides.showAllCities') || 'Show all cities'}
                </button>
              )}
              {rideTypeFilter && rideTypeFilter !== '' && (
                <button
                  onClick={() => setRideTypeFilter('')}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {t('rides.showAllTypes') || 'Show all types'}
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rides
            .filter((ride) => ride.departureDate) // Filter out rides without dates
            .map((ride) => {
              // Fix timezone issue: combine date and time in local timezone
              // departureDate from API is an ISO string like "2025-12-24T00:00:00.000Z" or "2025-12-24"
              // departureTime is like "07:00"
              // Extract date part and combine with time: "2025-12-24T07:00" which will be interpreted in local timezone
              const timeStr = ride.departureTime || '00:00';
              // Extract date part from ISO string (handles both "2025-12-24T00:00:00.000Z" and "2025-12-24")
              const dateStr = String(ride.departureDate).split('T')[0]; // Get "YYYY-MM-DD" from ISO string
              const dateTimeString = `${dateStr}T${timeStr}`;
              const rideDate = new Date(dateTimeString);
              
              // Skip if date is invalid (shouldn't happen after filter, but safety check)
              if (isNaN(rideDate.getTime())) {
                console.warn('Invalid date for ride:', ride.id, dateTimeString, 'Original:', ride.departureDate);
                return null;
              }

            return (
              <Link
                key={ride.id}
                to={`/rides/${ride.id}`}
                className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{ride.name}</h2>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{ride.description}</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <span className="font-medium">{t('rides.startLocation')}:</span>
                    <span className="ml-2">{ride.departureLocation}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium">{t('rides.startTime')}:</span>
                    <span className="ml-2">{format(rideDate, 'MMM d, yyyy h:mm a')}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium">{t('rides.distance')}:</span>
                    <span className="ml-2">{ride.distanceKm} km</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium">{t('rides.participants')}:</span>
                    <span className="ml-2">
                      {ride.participantCount} / {ride.maxRiders}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RidesPage;
